/**
 * Trash Service
 * Manages deleted files with 30-day retention
 */

export interface TrashItem {
  fileName: string;
  originalPath: string;
  deletedAt: string;
  deletedBy?: string;
  size: number;
  expiresAt: string;
}

export interface TrashData {
  [userId: string]: {
    [fileName: string]: TrashItem;
  };
}

const TRASH_STORAGE_KEY = 'myportal-trash';
const TRASH_RETENTION_DAYS = 30;

/**
 * Calculate expiry date (30 days from deletion)
 */
function getExpiryDate(deletedAt: Date): string {
  const expiry = new Date(deletedAt);
  expiry.setDate(expiry.getDate() + TRASH_RETENTION_DAYS);
  return expiry.toISOString();
}

/**
 * Get all trash items for a user
 */
export function getUserTrash(userId: string): { [fileName: string]: TrashItem } {
  try {
    const allTrash = localStorage.getItem(TRASH_STORAGE_KEY);
    if (!allTrash) return {};
    
    const trashData: TrashData = JSON.parse(allTrash);
    return trashData[userId] || {};
  } catch (error) {
    console.error('Error reading trash:', error);
    return {};
  }
}

/**
 * Move file to trash
 */
export function moveToTrash(
  userId: string,
  fileName: string,
  size: number,
  originalPath?: string
): void {
  try {
    const allTrash = localStorage.getItem(TRASH_STORAGE_KEY);
    const trashData: TrashData = allTrash ? JSON.parse(allTrash) : {};
    
    if (!trashData[userId]) {
      trashData[userId] = {};
    }
    
    const deletedAt = new Date();
    const trashItem: TrashItem = {
      fileName,
      originalPath: originalPath || fileName,
      deletedAt: deletedAt.toISOString(),
      size,
      expiresAt: getExpiryDate(deletedAt),
    };
    
    trashData[userId][fileName] = trashItem;
    localStorage.setItem(TRASH_STORAGE_KEY, JSON.stringify(trashData));
    
    console.log('File moved to trash:', fileName);
  } catch (error) {
    console.error('Error moving to trash:', error);
  }
}

/**
 * Restore file from trash
 */
export function restoreFromTrash(userId: string, fileName: string): void {
  try {
    const allTrash = localStorage.getItem(TRASH_STORAGE_KEY);
    if (!allTrash) return;
    
    const trashData: TrashData = JSON.parse(allTrash);
    
    if (trashData[userId] && trashData[userId][fileName]) {
      delete trashData[userId][fileName];
      localStorage.setItem(TRASH_STORAGE_KEY, JSON.stringify(trashData));
      console.log('File restored from trash:', fileName);
    }
  } catch (error) {
    console.error('Error restoring from trash:', error);
  }
}

/**
 * Permanently delete from trash
 */
export function permanentlyDelete(userId: string, fileName: string): void {
  try {
    const allTrash = localStorage.getItem(TRASH_STORAGE_KEY);
    if (!allTrash) return;
    
    const trashData: TrashData = JSON.parse(allTrash);
    
    if (trashData[userId] && trashData[userId][fileName]) {
      delete trashData[userId][fileName];
      localStorage.setItem(TRASH_STORAGE_KEY, JSON.stringify(trashData));
      console.log('File permanently deleted:', fileName);
    }
  } catch (error) {
    console.error('Error permanently deleting:', error);
  }
}

/**
 * Empty entire trash for user
 */
export function emptyTrash(userId: string): number {
  try {
    const allTrash = localStorage.getItem(TRASH_STORAGE_KEY);
    if (!allTrash) return 0;
    
    const trashData: TrashData = JSON.parse(allTrash);
    const itemCount = Object.keys(trashData[userId] || {}).length;
    
    if (trashData[userId]) {
      delete trashData[userId];
      localStorage.setItem(TRASH_STORAGE_KEY, JSON.stringify(trashData));
      console.log(`Emptied trash: ${itemCount} items`);
    }
    
    return itemCount;
  } catch (error) {
    console.error('Error emptying trash:', error);
    return 0;
  }
}

/**
 * Check if file is in trash
 */
export function isInTrash(userId: string, fileName: string): boolean {
  const userTrash = getUserTrash(userId);
  return !!userTrash[fileName];
}

/**
 * Get expired items (older than 30 days)
 */
export function getExpiredItems(userId: string): string[] {
  const userTrash = getUserTrash(userId);
  const now = new Date();
  const expired: string[] = [];
  
  Object.entries(userTrash).forEach(([fileName, item]) => {
    const expiryDate = new Date(item.expiresAt);
    if (expiryDate <= now) {
      expired.push(fileName);
    }
  });
  
  return expired;
}

/**
 * Auto-cleanup expired items
 */
export function cleanupExpiredItems(userId: string): string[] {
  const expired = getExpiredItems(userId);
  
  expired.forEach(fileName => {
    permanentlyDelete(userId, fileName);
  });
  
  if (expired.length > 0) {
    console.log(`Auto-cleanup: ${expired.length} expired items deleted`);
  }
  
  return expired;
}

/**
 * Get trash statistics
 */
export function getTrashStats(userId: string): {
  totalItems: number;
  totalSize: number;
  expiringSoon: number; // expires in < 7 days
} {
  const userTrash = getUserTrash(userId);
  const now = new Date();
  const sevenDaysFromNow = new Date();
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
  
  let totalSize = 0;
  let expiringSoon = 0;
  
  Object.values(userTrash).forEach(item => {
    totalSize += item.size;
    
    const expiryDate = new Date(item.expiresAt);
    if (expiryDate <= sevenDaysFromNow && expiryDate > now) {
      expiringSoon++;
    }
  });
  
  return {
    totalItems: Object.keys(userTrash).length,
    totalSize,
    expiringSoon,
  };
}

/**
 * Get days until expiry for a file
 */
export function getDaysUntilExpiry(expiresAt: string): number {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffTime = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

/**
 * Format time ago
 */
export function formatDeletedAt(deletedAt: string): string {
  const date = new Date(deletedAt);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffMinutes > 0) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  return 'Just now';
}

/**
 * Bulk restore multiple files
 */
export function bulkRestore(userId: string, fileNames: string[]): number {
  let restored = 0;
  fileNames.forEach(fileName => {
    if (isInTrash(userId, fileName)) {
      restoreFromTrash(userId, fileName);
      restored++;
    }
  });
  return restored;
}

/**
 * Bulk permanently delete multiple files
 */
export function bulkPermanentlyDelete(userId: string, fileNames: string[]): number {
  let deleted = 0;
  fileNames.forEach(fileName => {
    if (isInTrash(userId, fileName)) {
      permanentlyDelete(userId, fileName);
      deleted++;
    }
  });
  return deleted;
}

/**
 * Search trash items
 */
export function searchTrash(userId: string, searchTerm: string): { [fileName: string]: TrashItem } {
  const userTrash = getUserTrash(userId);
  const results: { [fileName: string]: TrashItem } = {};
  
  const lowerSearch = searchTerm.toLowerCase();
  
  Object.entries(userTrash).forEach(([fileName, item]) => {
    if (fileName.toLowerCase().includes(lowerSearch) || 
        item.originalPath.toLowerCase().includes(lowerSearch)) {
      results[fileName] = item;
    }
  });
  
  return results;
}
